import React from "react";
import {
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  Box,
} from "@mui/material";
import {
  CheckCircle as ActiveIcon,
  Archive as ArchivedIcon,
  Edit as DraftIcon,
} from "@mui/icons-material";
import { useSeason } from "../contexts/SeasonContext";

interface SeasonSelectorProps {
  variant?: "outlined" | "filled" | "standard";
  size?: "small" | "medium";
  showStatus?: boolean;
  fullWidth?: boolean;
  sx?: any;
  excludeDrafts?: boolean;
  allowAllSeasons?: boolean;
  hideLabel?: boolean;
}

const getSeasonStatusIcon = (status: string) => {
  switch (status) {
    case "active":
      return <ActiveIcon sx={{ fontSize: 18, color: "#4caf50" }} />;
    case "completed":
      return <ArchivedIcon sx={{ fontSize: 18, color: "#757575" }} />;
    case "draft":
      return <DraftIcon sx={{ fontSize: 18, color: "#2196f3" }} />;
    default:
      return null;
  }
};

const getSeasonStatusColor = (status: string) => {
  switch (status) {
    case "active":
      return "#4caf50";
    case "completed":
      return "#757575";
    case "draft":
      return "#2196f3";
    default:
      return "#1976d2";
  }
};

const getSeasonStatusLabel = (status: string) => {
  switch (status) {
    case "active":
      return "Active";
    case "completed":
      return "Completed";
    case "draft":
      return "Draft";
    default:
      return status;
  }
};

export default function SeasonSelector({
  variant = "outlined",
  size = "medium",
  showStatus = true,
  fullWidth = false,
  sx = {},
  excludeDrafts = false,
  allowAllSeasons = false,
  hideLabel = false,
}: SeasonSelectorProps) {
  const { selectedSeason, allSeasons, setSelectedSeason, isLoading } =
    useSeason();

  // Filter out draft seasons if excludeDrafts is true
  const filteredSeasons = excludeDrafts
    ? allSeasons.filter((s) => s.status !== "draft")
    : allSeasons;

  if (isLoading) {
    return (
      <FormControl
        variant={variant}
        size={size}
        fullWidth={fullWidth}
        sx={{ minWidth: 200, ...sx }}
      >
        {!hideLabel && <InputLabel>Season</InputLabel>}
        <Select disabled label={hideLabel ? undefined : "Season"} value="">
          <MenuItem value="">Loading...</MenuItem>
        </Select>
      </FormControl>
    );
  }

  return (
    <FormControl
      variant={variant}
      size={size}
      fullWidth={fullWidth}
      sx={{ minWidth: 200, ...sx }}
    >
      {!hideLabel && <InputLabel>Season</InputLabel>}
      <Select
        value={selectedSeason?.id || "all"}
        onChange={(e) =>
          setSelectedSeason(e.target.value === "all" ? null : e.target.value)
        }
        label={hideLabel ? undefined : "Season"}
        renderValue={(value) => {
          if (value === "all") {
            return (
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <span>All Seasons</span>
              </Box>
            );
          }

          const season = allSeasons.find((s) => s.id === value);
          if (!season) return value;

          return (
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              {showStatus && getSeasonStatusIcon(season.status)}
              <span>{season.name}</span>
              {showStatus && season.isActive && (
                <Chip
                  label="Active"
                  size="small"
                  sx={{
                    height: 20,
                    fontSize: "0.7rem",
                    bgcolor: getSeasonStatusColor("active"),
                    color: "white",
                  }}
                />
              )}
            </Box>
          );
        }}
      >
        {allowAllSeasons && (
          <MenuItem value="all">
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1,
                width: "100%",
              }}
            >
              <span style={{ flex: 1 }}>All Seasons</span>
            </Box>
          </MenuItem>
        )}
        {filteredSeasons.map((season) => (
          <MenuItem key={season.id} value={season.id}>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1,
                width: "100%",
              }}
            >
              {showStatus && getSeasonStatusIcon(season.status)}
              <span style={{ flex: 1 }}>{season.name}</span>
              {showStatus && (
                <Chip
                  label={getSeasonStatusLabel(season.status)}
                  size="small"
                  sx={{
                    height: 22,
                    fontSize: "0.75rem",
                    bgcolor: getSeasonStatusColor(season.status),
                    color: "white",
                  }}
                />
              )}
            </Box>
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  );
}
